import { fromJS, List, OrderedMap, Set } from 'immutable';
import get from 'lodash/get';

import * as Constants from '../constants';
import * as DomainUtils from '../domains/externalUtils';
import { createReducer } from '../lib/ReducerUtils';
import * as RouteUtils from '../lib/RouteUtils';
import Logger from '../tools/Logger';

const log = Logger.child({ component: 'reducers/StoreGarbageCollector' });

const MAX_EVENTS_SIZE = 200;
const DEFAULT_MAX_ENTITIES_SIZE = 1000;
const DEFAULT_MAX_PODCAST_LIST_SIZE = 500;
// Amount we'll invalidate when we reach max
const TRIM_PERCENT = 30;

// GARBAGE COLLECTOR REDUCER
export default createReducer(undefined, {
  [Constants.Playback.ActionTypes.ADD_TRACK_TO_STATION_HISTORY]: playlistTracksGC,
  [Constants.Api.ApiActions.AESOP_GET_DETAILS_SUCCESS]: invalidatePodcastState(),
  [Constants.Api.ApiActions.AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_SUCCESS]:
    invalidatePodcastState(),
  [Constants.Api.ApiActions.CATALOG_GET_DETAILS_SUCCESS]: invalidateEntities([
    'catalog',
    'details',
  ]),
  [Constants.Api.ApiActions.CATALOG_GET_ARTIST_DISCOGRAPHY_SUCCESS]: invalidateEntities([
    'catalog',
    'discography',
  ]),
  [Constants.Api.ApiActions.CATALOG_GET_DETAILS_WITH_COLLABORATIONS_SUCCESS]: invalidateEntities([
    'catalog',
    'details',
  ]),
  [Constants.Api.ApiActions.CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_SUCCESS]:
    invalidateEntities(['catalog', 'discography']),
  [Constants.Api.ApiActions.GET_ARTIST_EVENTS_SUCCESS]: invalidateEntities(
    ['catalog', 'events'],
    MAX_EVENTS_SIZE,
  ),
  [Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE]: apsGC,
  [Constants.Annotations.ActionTypes.OVERWRITE_ANNOTATIONS]: overwriteAnnotations,
  [Constants.Api.ApiActions.FETCH_STATIONS_SUCCESS]: stationsGC,
});

// CASE FUNCTIONS
function apsGC(state) {
  if (state.aps) {
    let pandoraIds = Set(
      DomainUtils.getInDomain('playback.selectors.getSessionItemPandoraIds')(state),
    );
    pandoraIds = pandoraIds.subtract(
      Set.of(DomainUtils.getInDomain('playback.selectors.getCurrentItemPandoraId')(state)),
    );
    pandoraIds = pandoraIds.subtract(
      Set.of(DomainUtils.getInDomain('playback.selectors.getNextItem')(state).get('pandoraId')),
    );
    pandoraIds = pandoraIds.subtract(
      Set(DomainUtils.getInDomain('playback.selectors.getSessionList', List())(state)),
    );
    pandoraIds = pandoraIds.subtract(
      Set(DomainUtils.getInDomain('playback.utils.getAllStationHistories', List())(state)),
    );
    for (const pandoraId of pandoraIds) {
      state.aps = state.aps.deleteIn([Constants.Playback.APSKeys.SESSION_ITEMS, pandoraId]);
    }
    return state;
  }
  return state;
}

function playlistTracksGC(state) {
  const ENTITIES_PLAYLIST_TRACKS_KEY = 'playlistTracks';
  const reducersWithReferences = ['stations', 'playlist', 'audioAds'];

  let allPlaylistTrackTokens = new Set(state.entities.get(ENTITIES_PLAYLIST_TRACKS_KEY).keys());

  reducersWithReferences.forEach(reducerKey => {
    if (state[reducerKey] && state[reducerKey].toKeyedSeq) {
      const flattenedReducer = state[reducerKey].toKeyedSeq().flatten();
      for (const entry of flattenedReducer.entries()) {
        const key = entry[0];
        const value = entry[1];
        if (allPlaylistTrackTokens.has(key)) {
          allPlaylistTrackTokens = allPlaylistTrackTokens.delete(key);
        }
        if (allPlaylistTrackTokens.has(value)) {
          allPlaylistTrackTokens = allPlaylistTrackTokens.delete(value);
        }
      }
    }
  });
  allPlaylistTrackTokens.forEach(trackToken => {
    state.entities = state.entities.deleteIn([ENTITIES_PLAYLIST_TRACKS_KEY, trackToken]);
  });
  return state;
}

function stationsGC(state, action) {
  const collectedStations = fromJS(get(action, 'response.entities.stations', {}));
  const routing = RouteUtils.getRouterCurrentLocation(state);
  const backstageRouteId = RouteUtils.getStationIdFromStationDetailsPath(routing.pathname);
  const shuffleStationId = DomainUtils.getInDomain('playback.utils.getShuffleStationId')(state);
  const stationEntities = state.entities.get('stations').filter((entity, stationId) => {
    return (
      collectedStations.get(stationId) ||
      stationId === backstageRouteId ||
      stationId === shuffleStationId
    );
  });

  if (stationEntities.isEmpty() && !collectedStations.isEmpty()) {
    log.error(
      'stationEntities should not be empty if collectedStations contains any number of stations',
    );
  }

  state.entities = state.entities.set('stations', stationEntities);
  return state;
}

function overwriteAnnotations(state, { payload }) {
  state.entities = state.entities.setIn(['catalog', 'annotations'], payload.annotations);
  return state;
}

function invalidateEntities(path, maxSize = DEFAULT_MAX_ENTITIES_SIZE) {
  const endSlice = Math.round(maxSize * ((100 - TRIM_PERCENT) / 100));
  return state => {
    const entitiesToGC = state.entities.getIn(path);
    if (entitiesToGC && entitiesToGC.size > maxSize) {
      log.info(
        `invalidateEntities(): garbage collecting -> ${path} : ${entitiesToGC.size} > ${maxSize}`,
      );
      state.entities = state.entities.setIn(path, entitiesToGC.slice(0, endSlice));
    }
    return state;
  };
}

function invalidatePodcastState(maxSize = DEFAULT_MAX_PODCAST_LIST_SIZE) {
  return state => {
    const PodcastKeys = DomainUtils.getInDomain('playback.constants.PodcastKeys');
    const podcastPaths = [
      PodcastKeys.PROGRESS,
      PodcastKeys.NEW_EPISODE_BADGE,
      PodcastKeys.USER_PROGRAM_DETAILS,
    ];
    const newSize = Math.round(maxSize * ((100 - TRIM_PERCENT) / 100));
    state.podcast = podcastPaths.reduce((podcast, path) => {
      return podcast.update(path, OrderedMap(), orderedMap => {
        if (orderedMap.size > maxSize) {
          log.info(
            `invalidatePodcastState(): garbage collecting -> podcast.${path} : ${orderedMap.size} > ${maxSize}`,
          );
          return orderedMap.take(newSize);
        }
        return orderedMap;
      });
    }, state.podcast);
    return state;
  };
}
